import { useCallback } from 'react';

import { showFlag } from '@trello/nachos/experimental-flags';

import { l } from 'app/scripts/lib/localize';
import { useBoardPremiumFeaturesFragment } from './BoardPremiumFeaturesFragment.generated';

const largeFileSizeLimitInBytes = 250 * 1024 * 1024;
const smallFileSizeLimitInBytes = 10 * 1024 * 1024;

export const useFileSizeAlert = ({ boardId }: { boardId: string | null }) => {
  const { data: board, complete } = useBoardPremiumFeaturesFragment({
    from: { id: boardId },
    optimistic: true,
  });
  return {
    warnIfFileTooLarge: useCallback(
      ({ fileSizeInBytes }: { fileSizeInBytes: number }) => {
        let hasLargeAttachmentsSupport = false;
        if (board) {
          hasLargeAttachmentsSupport = Boolean(
            board?.premiumFeatures.includes('largeAttachments'),
          );
        }
        const sizeLimitInBytes = hasLargeAttachmentsSupport
          ? largeFileSizeLimitInBytes
          : smallFileSizeLimitInBytes;

        return new Promise<boolean>((resolve, reject) => {
          if (complete && board) {
            const isTooLarge = fileSizeInBytes > sizeLimitInBytes;
            if (isTooLarge) {
              const title = hasLargeAttachmentsSupport
                ? l('alerts.file size exceeds 250mb')
                : l('alerts.file size exceeds 10mb');
              showFlag({
                id: 'upload',
                title,
                appearance: 'error',
                isAutoDismiss: true,
              });
            }
            resolve(isTooLarge);
          } else if (!complete) {
            reject(new Error('Board premium features did not load'));
          }
        });
      },
      [board, complete],
    ),
  };
};
